/* global window */
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Logger } from '@gitbook/util-debug';
import { useMergedRefs, useResizeObserver } from '@gitbook/util-react-hooks';
import { assertIsDefined } from '@gitbook/util-ts';

import { resolveDynamicBinding } from './bindings';
import type { ContentKitElementProps } from './types';

const logger = Logger('contentkit:webframe');

export function ContentKitWebFrame(
    props: ContentKitElementProps<api.ContentKitWebFrame>
): React.ReactElement {
    const { element, localState, dispatchAction, security } = props;
    const iframeRef = React.useRef<HTMLIFrameElement>(null);

    const [size, setSize] = React.useState<{
        height?: number;
        aspectRatio?: number;
    }>({});

    const readyRef = React.useRef(false);
    const messagesQueueRef = React.useRef<object[]>([]);
    const target = React.useMemo(() => new URL(element.source.url), [element.source.url]);
    const isSecureDomain = security.firstPartyDomains.includes(target.host);

    const sendMessage = React.useCallback(
        (message: object) => {
            if (!iframeRef.current) {
                return;
            }

            // For security reasons, only iframe from our integrations domains are allowed
            // to send and receive messages
            if (!isSecureDomain) {
                return;
            }

            if (readyRef.current) {
                logger.debug('send message', message);
                assertIsDefined(iframeRef.current.contentWindow, 'content window not found');
                iframeRef.current.contentWindow.postMessage(
                    message,
                    `${target.protocol}//${target.host}`
                );
            } else {
                logger.debug('queue message', message);
                messagesQueueRef.current.push(message);
            }
        },
        [target, isSecureDomain]
    );

    //
    // Listen to message coming from the webframe
    //
    React.useEffect(() => {
        window.addEventListener('message', (event) => {
            if (!iframeRef.current) {
                return;
            }

            const message = event.data;
            const origin = new URL(event.origin);

            // For security reasons, only iframe from our integrations domains are allowed
            // to send and receive messages
            if (!security.firstPartyDomains.includes(origin.host)) {
                return;
            }

            const contentWindow = iframeRef.current.contentWindow;

            // Discard any messages coming other potential webframes on the page.
            if (!contentWindow || event.source !== contentWindow) {
                return;
            }

            if (typeof message === 'string') {
                try {
                    // We support the default oembed iframe protocol
                    // https://docs.embed.ly/reference/provider-height-resizing
                    const parsed = JSON.parse(message);
                    if (parsed.context === 'iframe.resize' && typeof parsed.height === 'number') {
                        const width = contentWindow.outerWidth;
                        const height = parsed.height;

                        setSize({
                            aspectRatio: width / height,
                            height: height,
                        });
                    }
                } catch (_err) {
                    logger.warning('invalid message', message);
                    return;
                }
            }

            logger.debug('receive message from frame', message, 'from', event.origin);
            if (message.action) {
                switch (message.action.action) {
                    case '@webframe.ready':
                        readyRef.current = true;
                        messagesQueueRef.current.forEach((message) => {
                            sendMessage(message);
                        });
                        messagesQueueRef.current = [];
                        break;
                    case '@webframe.resize':
                        setSize((size) => ({
                            aspectRatio:
                                typeof message.action.size.aspectRatio !== 'undefined'
                                    ? Number(message.action.size.aspectRatio)
                                    : size.aspectRatio,

                            height: (() => {
                                if (typeof message.action.size.height !== 'undefined') {
                                    return Number(message.action.size.height);
                                }

                                // maxHeight was used prior to moving to height, maintain it for backward compatibility.
                                if (typeof message.action.size.maxHeight !== 'undefined') {
                                    return Number(message.action.size.maxHeight);
                                }

                                return size.height;
                            })(),
                        }));
                        break;
                    default:
                        dispatchAction(message.action);
                }
            }
        });
    }, [dispatchAction, sendMessage]);

    //
    // Send data to the webframe
    //
    React.useEffect(() => {
        if (!element.data) {
            return;
        }

        const state: Record<string, string> = {};
        Object.entries(element.data).forEach(([key, value]) => {
            state[key] = resolveDynamicBinding(localState, value);
        });

        return sendMessage({ state });
    }, [element.data, localState, sendMessage]);

    const [iframeWidth, setIframeWidth] = React.useState<number>(0);
    const measureRef = useResizeObserver((entry) => {
        setIframeWidth(entry.contentRect.width);
    });

    const mergedRefs = useMergedRefs(iframeRef, measureRef);

    const aspectRatio = size.aspectRatio || element.aspectRatio;

    // Compute the original width based on the aspect ratio and height.
    const originalWidth =
        size.height && size.aspectRatio ? size.height * size.aspectRatio : undefined;

    // Compute the height of the iframe:
    // - If the iframe is shorter than the original width, we use the aspect ratio to compute the height.
    // - If the iframe width is larger than the original width, we use the provided height
    //   or default to '100%' if not specified.
    const height =
        originalWidth && aspectRatio && originalWidth > iframeWidth
            ? Math.max(Math.round(iframeWidth / aspectRatio), 32)
            : size.height
              ? Math.max(size.height, 32)
              : '100%';

    return (
        <iframe
            ref={mergedRefs}
            src={element.source.url}
            title={element.source.url}
            allowFullScreen
            allow="clipboard-write"
            className="contentkit-webframe"
            style={{
                width: '100%',
                maxWidth: '100%',
                aspectRatio,
                height,
                border: 'none',
            }}
        />
    );
}
